import axios, { InternalAxiosRequestConfig } from 'axios';
import { appConfig } from '../configs/appConfig';
import {
  LOCAL_STORAGE_PSA,
  LOCAL_STORAGE_REFRESH_TOKEN,
  LOCAL_STORAGE_TOKEN,
} from '../constants/storage';
import { LOGIN } from '../router/routes';
import { requestResources } from './requestResources';
import { deleteAllNotificationsForUser } from '../utils/indexedDB';
import { decodeAccessToken } from '../utils/getDecodedAccessToken';

const axiosInstance = axios.create({
  baseURL: appConfig.baseUrl,
  withCredentials: false,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE',
    'Access-Control-Allow-Headers': 'Content-Type',
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    serialize: params => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(item => {
            searchParams.append(key, String(item));
          });
        } else if (value !== null && value !== undefined) {
          searchParams.append(key, String(value));
        }
      });

      return searchParams.toString();
    },
  },
});

axiosInstance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const accessToken = localStorage.getItem(LOCAL_STORAGE_TOKEN);

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  if (config.url && config.url.includes(requestResources.clientPortalTickets)) {
    config.url = config.url.replace(
      requestResources.clientPortalTickets,
      `${requestResources.clientPortalTickets}/${localStorage.getItem(LOCAL_STORAGE_PSA)}`,
    );
  }

  return config;
});

axiosInstance.interceptors.response.use(
  res => res,
  res => {
    if (res.response.status === 401) {
      // Clear notifications before logout
      try {
        const token = localStorage.getItem(LOCAL_STORAGE_TOKEN);
        if (token) {
          const decoded = decodeAccessToken(token);
          deleteAllNotificationsForUser(decoded.contactId).catch(error => {
            console.error('Failed to delete notifications on 401:', error);
          });
        }
      } catch {
        // If token decode fails, continue with logout
      }

      localStorage.removeItem(LOCAL_STORAGE_TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN);
      return;
    }

    const { response } = res;

    const errorMessage = `RequestError: ${(response.data.message || response.data.detail) || 'Something went wrong'}`;
    throw new Error(errorMessage);
  },
);

export default axiosInstance;
